import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {useReportRunsInfiniteQuery} from './useReportRunsQuery';
import {useReportRunsSearch} from './useReportRunsSearch';
import {useReportRunsTableColumns} from './useReportRunsTableColumns';
import {ReportRunsFilters} from './useReportRunsFilter';
import {ReportRun} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created` is stamped when the run is
 * requested and never moves. `succeededAt` would be the wrong choice twice
 * over: it is null for every pending run, and it moves when a run completes —
 * reordering the list under an operator who is watching it.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ReportRun> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * report run id (`frr_...`) is what an operator pastes in from the Stripe
 * dashboard; `error` because searching for a failure message is how someone
 * finds every run that broke the same way.
 */
const matchesKeyword = (item: ReportRun, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.reportType,
        item.status,
        item.error,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: ReportRun, filters: ReportRunsFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof ReportRun];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listReportRuns($limit, $nextToken)` accepts no filter and no sort argument,
 * so the server can neither narrow nor order the result set. Everything below
 * therefore operates on the loaded window only. Sorting page 1 does not sort
 * the dataset, and a keyword matches only rows the browser has already
 * received — the other rows were never examined.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 */
export const useReportRuns = (filters: ReportRunsFilters = {}) => {
    const query = useReportRunsInfiniteQuery();
    const {columns} = useReportRunsTableColumns();
    const {keyword} = useReportRunsSearch();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: ReportRun[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // ⚠️ Stripe hands back epoch SECONDS; moment parses a bare number as
        // MILLISECONDS. Normalise ONCE, here, so display and sort agree — and
        // so no column has to cast a number through `as unknown as string`,
        // which is what hid this bug: every run rendered as January 1970.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
            succeededAtMillis: typeof item.succeededAt === 'number'
                ? item.succeededAt * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong,
    // and this table has a genuinely nullable sort key in succeededAt.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<ReportRun>(matched, DEFAULT_SORT_STATUS);

    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
